import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format JSON data as readable text
 * Handles strings, objects, arrays, and nested structures
 */
export function formatJsonAsText(data: any): string {
  if (!data) return '';

  try {
    let parsedData = data;

    // If it's a string, try to parse it as JSON
    if (typeof data === 'string') {
      // Check if it looks like JSON (starts with { or [)
      const trimmed = data.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          parsedData = JSON.parse(data);
        } catch (parseError) {
          // If JSON parsing fails, return the original string
          return data;
        }
      } else {
        // If it doesn't look like JSON, return as is
        return data;
      }
    }

    // If it's an object, format it nicely
    if (typeof parsedData === 'object' && parsedData !== null) {
      return JSON.stringify(parsedData, null, 2);
    }

    // For other types, convert to string
    return String(parsedData);
  } catch (error) {
    // If anything goes wrong, return a string representation
    return String(data);
  }
}

/**
 * Convert ArrayBuffer to Base64 string (browser-compatible)
 * This replaces the Node.js Buffer.from() method which isn't available in browsers
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

/**
 * Get container-friendly path from a full storage path
 * Extracts the path starting from 'users/' if present
 */
export function getContainerFriendlyPath(path: string): string {
  // If path starts with 'users/', it's already a relative path
  if (path.startsWith('users/')) {
    return path;
  }

  // Extract the relative path from the absolute path
  // Assuming the format is something like '/path/to/storage/users/userId/...'
  const userIndex = path.indexOf('users/');
  if (userIndex !== -1) {
    return path.substring(userIndex);
  }

  // If we can't find 'users/', return the original path
  return path;
}